import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '../ui/Button';
import Container from './Container';
import Logo from '../ui/Logo';

const Header: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Features', path: '/features' },
    { label: 'Pricing', path: '/pricing' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Blog', path: '/blog' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
  ];

  const toggleMenu = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <Container>
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="relative z-10" aria-label="Home">
            <Logo />
          </Link>

          {/* Desktop Nav */}
          <nav
            className="hidden md:flex items-center space-x-8"
            aria-label="Main navigation"
          >
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`font-medium transition-colors hover:text-primary-600 ${
                  location.pathname === item.path
                    ? 'text-primary-600'
                    : scrolled
                    ? 'text-neutral-900'
                    : 'text-neutral-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Button variant="primary" size="sm" href="https://rainautomation.com/agent-os">
              Get Started
            </Button>
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={toggleMenu}
            className="md:hidden relative z-10 text-neutral-900"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </Container>

      {/* Mobile Nav Drawer */}
      <motion.div
        role="dialog"
        aria-label="Mobile menu"
        aria-hidden={!isOpen}
        className={`fixed inset-0 bg-white z-40 md:hidden ${
          isOpen ? 'block' : 'hidden'
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={isOpen ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        <Container>
          <div className="flex flex-col pt-24 pb-8 h-full">
            <nav className="flex flex-col space-y-6" aria-label="Mobile navigation">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-xl font-medium ${
                    location.pathname === item.path
                      ? 'text-primary-600'
                      : 'text-neutral-900'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="flex flex-col space-y-4 pt-6">
                <Button variant="primary" size="lg" fullWidth href="https://rainautomation.com/agent-os">
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        </Container>
      </motion.div>
    </header>
  );
};

export default Header;
